import type { PayloadAction } from '@reduxjs/toolkit'

import type {
  DataItem,
  EndpointConfig,
  OperatorOption,
  PickerSliceState,
  TopDropdownState,
} from './types'
import { createInitialTopState, DEFAULT_GROUP_ID, ensurePicker } from './types'

// ═══════════════════════════════════════════════════════════════════════════
// Reducer Cases
// ═══════════════════════════════════════════════════════════════════════════

export const topReducers = {
  loadTopData: (
    state: PickerSliceState,
    action: PayloadAction<{
      pickerId: number
      groupId?: string
      data: DataItem[]
      totalCount?: number
      pageSize?: number
    }>
  ) => {
    const {
      pickerId,
      groupId = DEFAULT_GROUP_ID,
      data,
      totalCount,
      pageSize,
    } = action.payload
    const picker = ensurePicker(state, pickerId, groupId)
    const dataHash: Record<string, DataItem> = {}
    for (const item of data) {
      dataHash[item.id] = item
    }
    picker.top.data = dataHash
    if (totalCount !== undefined) {
      picker.top.totalCount = totalCount
    }
    if (pageSize !== undefined) {
      picker.top.pageSize = pageSize
    }
  },

  setTopSearchTerm: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; term: string }>
  ) => {
    const { pickerId, term } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.searchTerm = term
      picker.top.page = 1
    }
  },

  setTopPage: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; page: number }>
  ) => {
    const { pickerId, page } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.page = page
    }
  },

  setTopTotalCount: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; totalCount: number }>
  ) => {
    const { pickerId, totalCount } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.totalCount = totalCount
    }
  },

  setTopSelectedItem: (
    state: PickerSliceState,
    action: PayloadAction<{
      pickerId: number
      groupId?: string
      key: string | null
    }>
  ) => {
    const { pickerId, groupId = DEFAULT_GROUP_ID, key } = action.payload
    const picker = ensurePicker(state, pickerId, groupId)
    picker.top.selectedItem = key
    // Store the selected item's data separately
    if (key && picker.top.data[key]) {
      picker.top.selectedItemData = picker.top.data[key]
    } else if (key === null) {
      picker.top.selectedItemData = null
    }
    // Note: if key is set but not in data, we keep existing selectedItemData
  },

  clearTopSelection: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number }>
  ) => {
    const { pickerId } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.selectedItem = null
      picker.top.selectedItemData = null
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Configuration Actions
  // ═══════════════════════════════════════════════════════════════════════════

  setTopLabel: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; label: string }>
  ) => {
    const { pickerId, label } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.label = label
    }
  },

  setTopOperator: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; operator: string }>
  ) => {
    const { pickerId, operator } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.operator = operator
    }
  },

  setTopOperatorOptions: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; options: OperatorOption[] }>
  ) => {
    const { pickerId, options } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.operatorOptions = options
    }
  },

  setTopEndpointConfig: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; config: EndpointConfig }>
  ) => {
    const { pickerId, config } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.endpointConfig = config
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Open/Close Actions
  // ═══════════════════════════════════════════════════════════════════════════

  setTopIsOpen: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; isOpen: boolean }>
  ) => {
    const { pickerId, isOpen } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.isOpen = isOpen
    }
  },

  toggleTopIsOpen: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number }>
  ) => {
    const { pickerId } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.isOpen = !picker.top.isOpen
    }
  },

  openTop: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number }>
  ) => {
    const { pickerId } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.isOpen = true
    }
  },

  closeTop: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number }>
  ) => {
    const { pickerId } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.top.isOpen = false
    }
  },
}

// ═══════════════════════════════════════════════════════════════════════════
// Selectors (flat structure - only need pickerId)
// ═══════════════════════════════════════════════════════════════════════════

type RootState = { picker: PickerSliceState }

const defaultTopState = createInitialTopState()

export const selectTop = (
  state: RootState,
  pickerId: number
): TopDropdownState => state.picker.pickers[pickerId]?.top ?? defaultTopState

export const selectTopData = (
  state: RootState,
  pickerId: number
): Record<string, DataItem> => state.picker.pickers[pickerId]?.top.data ?? {}

export const selectTopSelectedItem = (
  state: RootState,
  pickerId: number
): string | null => state.picker.pickers[pickerId]?.top.selectedItem ?? null

export const selectTopSelectedItemData = (
  state: RootState,
  pickerId: number
): DataItem | null =>
  state.picker.pickers[pickerId]?.top.selectedItemData ?? null

export const selectTopSearchTerm = (
  state: RootState,
  pickerId: number
): string => state.picker.pickers[pickerId]?.top.searchTerm ?? ''

export const selectTopPage = (state: RootState, pickerId: number): number =>
  state.picker.pickers[pickerId]?.top.page ?? 1

export const selectTopTotalCount = (
  state: RootState,
  pickerId: number
): number => state.picker.pickers[pickerId]?.top.totalCount ?? 0

export const selectTopPageSize = (state: RootState, pickerId: number): number =>
  state.picker.pickers[pickerId]?.top.pageSize ?? 50

export const selectTopLabel = (state: RootState, pickerId: number): string =>
  state.picker.pickers[pickerId]?.top.label ?? ''

export const selectTopOperator = (state: RootState, pickerId: number): string =>
  state.picker.pickers[pickerId]?.top.operator ?? 'any'

export const selectTopOperatorOptions = (
  state: RootState,
  pickerId: number
): OperatorOption[] => state.picker.pickers[pickerId]?.top.operatorOptions ?? []

export const selectTopEndpointConfig = (
  state: RootState,
  pickerId: number
): EndpointConfig | null =>
  state.picker.pickers[pickerId]?.top.endpointConfig ?? null

export const selectTopIsOpen = (state: RootState, pickerId: number): boolean =>
  state.picker.pickers[pickerId]?.top.isOpen ?? false

// ═══════════════════════════════════════════════════════════════════════════
// Selector to get picker's group ID
// ═══════════════════════════════════════════════════════════════════════════

export const selectPickerGroupId = (
  state: RootState,
  pickerId: number
): string | undefined => state.picker.pickerToGroup[pickerId]
